/* eslint-disable */
import { getAPI } from "@/axios-api";

/**
 * Fetch users for the organization
 * @param {string|number} organizationId - The organization ID (optional, defaults to localStorage)
 * @returns {Promise<Array|null>} The users data or null if not found
 */
export const fetchUsers = async (organizationId) => {
  try {
    const org = organizationId || localStorage.getItem('organisation');
    if (!org) {
      console.error('No organization ID provided');
      return null;
    }

    const response = await getAPI.get(`accounts/users/organisation/${org}/?pageSize=5/`);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error fetching users:', error);
    return null;
  }
};

/**
 * Fetch services for the organization
 * @param {string|number} organizationId - The organization ID (optional, defaults to localStorage)
 * @returns {Promise<Array|null>} The services data or null if not found
 */
export const fetchServices = async (organizationId) => {
  try {
    const org = organizationId || localStorage.getItem('organisation');
    if (!org) {
      console.error('No organization ID provided');
      return null;
    }

    const response = await getAPI.get(`services/?organisation=${org}`);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error fetching services:', error);
    return null;
  }
};

/**
 * Fetch groups for the organization
 * @returns {Promise<Array|null>} The groups data or null if not found
 */
export const fetchGroups = async () => {
  try {
    const response = await getAPI.get('groupes/');
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error fetching groups:', error);
    return null;
  }
};

/**
 * Create a new user
 * @param {Object} userData - The user data to create
 * @returns {Promise<Object|null>} The created user data or null if failed
 */
export const createUser = async (userData) => {
  try {
    const response = await getAPI.post('accounts/register/add-user', userData);
    if (response.status === 200 || response.status === 201) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error creating user:', error);
    return null;
  }
};

/**
 * Create multiple users
 * @param {Object} usersData - The users data containing users array
 * @returns {Promise<Object|null>} The created users data or null if failed
 */
export const createUsers = async (usersData) => {
  try {
    const response = await getAPI.post('accounts/register/add-users', usersData);
    if (response.status === 200 || response.status === 201) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error creating users:', error);
    return null;
  }
};

/**
 * Update a user
 * @param {string|number} userId - The user ID to update
 * @param {Object} userData - The user data to update
 * @returns {Promise<Object|null>} The updated user data or null if failed
 */
export const updateUser = async (userId, userData) => {
  try {
    const response = await getAPI.patch(`accounts/users/manager/${userId}/`, userData);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error(`Error updating user with ID ${userId}:`, error);
    return null;
  }
};

/**
 * Update user role
 * @param {string|number} userId - The user ID to update
 * @returns {Promise<Object|null>} The updated user data or null if failed
 */
export const updateUserRole = async (userId, role) => {
  try {
    const response = await getAPI.post(`accounts/users/promote-user`, {
      "user_id": userId,
      "role": role
    });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error(`Error updating role for user with ID ${userId}:`, error);
    throw error;
  }
}

/**
 * Delete a user
 * @param {string|number} email - The user ID to delete
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export const deleteUser = async (email) => {
  try {
    const response = await getAPI.delete(`organisations/remove/user/`, {
      data: { email: email }
    });
    return response.status === 200 || response.status === 204;
  } catch (error) {
    console.error(`Error deleting user with ID ${userId}:`, error);
    throw error
    return false;
  }
};

/**
 * Create a new service
 * @param {Object} serviceData - The service data containing name and organisation
 * @returns {Promise<Object|null>} The created service data or null if failed
 */
export const createService = async (serviceData) => {
  try {
    const payload = {
      name: serviceData.name,
      organisation: localStorage.getItem('organisation') || serviceData.organisation
    }
    if (serviceData.responsible) payload.responsible = serviceData.responsible
    if (serviceData.auto_report_enabled !== undefined) payload.auto_report_enabled = serviceData.auto_report_enabled
    if (serviceData.selectedUsers?.length) payload.users = serviceData.selectedUsers
    const response = await getAPI.post('services/', payload);
    if (response.status === 200 || response.status === 201) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error('Error creating service:', error);
    return null;
  }
};

/**
 * Update a service (users via add_or_update_user_to_service)
 * @param {string|number} serviceId - The service ID to update
 * @param {Object} serviceData - The service data to update
 * @returns {Promise<Object|null>} The updated service data or null if failed
 */
export const updateService = async (serviceId, serviceData) => {
  try {
    const response = await getAPI.patch(`services/add_or_update_user_to_service/${serviceId}`, serviceData);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error(`Error updating service with ID ${serviceId}:`, error);
    return null;
  }
};

/**
 * Patch service metadata (responsible, auto_report_enabled)
 * @param {string|number} serviceId - The service ID to update
 * @param {Object} patchData - { responsible?, auto_report_enabled? }
 * @returns {Promise<Object|null>} The updated service data or null if failed
 */
export const patchService = async (serviceId, patchData) => {
  try {
    const response = await getAPI.patch(`services/${serviceId}/`, patchData);
    if (response.status === 200) {
      return response.data?.data ?? response.data;
    }
    return null;
  } catch (error) {
    console.error(`Error patching service ${serviceId}:`, error);
    return null;
  }
};

/**
 * Delete a service
 * @param {string|number} serviceId - The service ID to delete
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export const deleteService = async (serviceId) => {
  try {
    const response = await getAPI.delete(`services/${serviceId}/`);
    return response.status === 200 || response.status === 204;
  } catch (error) {
    console.error(`Error deleting service with ID ${serviceId}:`, error);
    return false;
  }
};

/**
 * Get users for a specific service
 * @param {string|number} serviceId - The service ID
 * @returns {Promise<Array|null>} The service users data or null if not found
 */
export const getServiceUsers = async (serviceId) => {
  try {
    const response = await getAPI.get(`services/get_users?service=${serviceId}`);
    if (response.status === 200) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error(`Error fetching users for service ${serviceId}:`, error);
    return null;
  }
};

/**
 * Create a new group
 * @param {Object} groupData - The group data containing name and users
 * @returns {Promise<Object|null>} The created group data or null if failed
 */
export const createGroup = async (groupData) => {
  try {
    const response = await getAPI.post('groupes/', groupData);
    if (response.status === 200 || response.status === 201) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error('Error creating group:', error);
    throw error
    return null;
  }
};

/**
 * Update a group
 * @param {string|number} groupId - The group ID to update
 * @param {Object} groupData - The group data to update
 * @returns {Promise<Object|null>} The updated group data or null if failed
 */
export const updateGroup = async (groupId, groupData) => {
  try {
    const response = await getAPI.patch(`groupes/${groupId}/`, groupData);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error(`Error updating group with ID ${groupId}:`, error);
    return null;
  }
};

/**
 * Delete a group
 * @param {string|number} groupId - The group ID to delete
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export const deleteGroup = async (groupId) => {
  try {
    const response = await getAPI.delete(`groupes/${groupId}/`);
    return response.status === 200 || response.status === 204;
  } catch (error) {
    console.error(`Error deleting group with ID ${groupId}:`, error);
    return false;
  }
};

/**
 * Get group details by ID
 * @param {string|number} groupId - The group ID
 * @returns {Promise<Object|null>} The group data or null if not found
 */
export const getGroupById = async (groupId) => {
  try {
    const response = await getAPI.get(`groupes/${groupId}/`);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error(`Error fetching group with ID ${groupId}:`, error);
    return null;
  }
};

/**
 * Get users statistics
 * @returns {Promise<Object|null>} The users statistics or null if not found
 */
export const getUsersStats = async () => {
  try {
    const response = await getAPI.get('accounts/invitation_statistics/');
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error fetching users statistics:', error);
    return null;
  }
};

/**
 * Send invitations to users
 * @param {Object} invitationData - The invitation data containing emails and other info
 * @returns {Promise<Object|null>} The invitation response or null if failed
 */
export const sendInvitations = async (invitationData) => {
  try {
    const response = await getAPI.post('organisations/send/invitations/', invitationData);
    if (response.status === 200 || response.status === 201) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error sending invitations:', error);
    return null;
  }
};

/**
 * Update service for user
 * @param {Object} serviceUserData - The service user data
 * @returns {Promise<Object|null>} The updated service user data or null if failed
 */
export const updateServiceForUser = async (serviceUserData) => {
  try {
    const response = await getAPI.post('services/update_service_for_user', serviceUserData);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error updating service for user:', error);
    return null;
  }
};

/**
 * Change user status
 * @param {string|number} userId - The user ID
 * @param {Object} statusData - The status data
 * @returns {Promise<Object|null>} The updated user data or null if failed
 */
export const changeUserStatus = async (userId, statusData) => {
  try {
    const response = await getAPI.put(`accounts/users/change-statut/${userId}/`, statusData);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error(`Error changing status for user ${userId}:`, error);
    return null;
  }
};

/**
 * Resend activation email
 * @param {Object} emailData - The email data containing user email
 * @returns {Promise<Object|null>} The response or null if failed
 */
export const resendActivationEmail = async (emailData) => {
  try {
    const response = await getAPI.post('accounts/resend-email-activate-account/', emailData);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error resending activation email:', error);
    return null;
  }
};

/**
 * Get user by ID
 * @param {string|number} userId - The user ID
 * @returns {Promise<Object|null>} The user data or null if not found
 */
export const getUserById = async (userId) => {
  try {
    const response = await getAPI.get(`accounts/user/${userId}/`);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error(`Error fetching user with ID ${userId}:`, error);
    return null;
  }
};

/**
 * Activate user account
 * @param {string|number} userId - The user ID
 * @returns {Promise<Object|null>} The activation response or null if failed
 */
export const activateUser = async (userId) => {
  try {
    const response = await getAPI.post(`accounts/users/${userId}/activate/`);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error(`Error activating user with ID ${userId}:`, error);
    return null;
  }
};

// Add these methods
export const getUser = async (userId) => {
  return await getAPI.get(`accounts/user/${userId}/`)
}

/*export const updateUser = async (userId, userData) => {
  return await getAPI.patch(`accounts/users/manager/${userId}/`, userData)
}*/

export const updateUserService = async (data) => {
  return await getAPI.post(`services/update_service_for_user`, data)
}

/**
 * Create multiple users with bulk registration
 * @param {Object} bulkUserData - The bulk user data containing users array
 * @returns {Promise<Object|null>} The created users response or null if failed
 */
export const createBulkUsers = async (bulkUserData) => {
  try {
    const response = await getAPI.post('accounts/register/add-users', bulkUserData);
    if (response.status === 200 || response.status === 201) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error creating bulk users:', error);
    throw error;
  }
};

/**
 * Send bulk invitations to multiple users
 * @param {Object} invitationData - The invitation data containing emails, service, and message
 * @returns {Promise<Object|null>} The invitation response or null if failed
 */
export const sendBulkInvitations = async (invitationData) => {
  try {
    const response = await getAPI.post('organisations/send/invitations/', invitationData);
    if (response.status === 200 || response.status === 201) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error sending bulk invitations:', error);
    throw error;
  }
};

/**
 * Send single invitation to a user
 * @param {Object} invitationData - The invitation data containing email, service, and message
 * @returns {Promise<Object|null>} The invitation response or null if failed
 */
export const sendSingleInvitation = async (invitationData) => {
  try {
    const response = await getAPI.post('organisations/send/invitations/', invitationData);
    if (response.status === 200 || response.status === 201) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error sending single invitation:', error);
    throw error;
  }
};

/**
 * Create a single user with registration
 * @param {Object} userData - The user data to create
 * @returns {Promise<Object|null>} The created user response or null if failed
 */
export const createSingleUser = async (userData) => {
  try {
    const response = await getAPI.post('accounts/register/add-users', { users: userData });
    if (response.status === 200 || response.status === 201) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error creating single user:', error);
    throw error;
  }
};